import { SignatoryRegime, AuthorisedSignatory, Beneficiary } from "@models/clients/clients.details/signatoryRegime";
import { TableColumn } from "@models/components/tableColumn";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";

export class AddSignatoryRegimeModalController {
  public static $inject = ['$mdDialog', '$scope', 'clientDetailsService', '$element', 'clientCRMRepo', '$rootScope'];
  
  public client: any;
  public signatoryRegime: Partial<SignatoryRegime> = {};
  public dropdownOptions: any = {};
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public title: string = 'New Signatory Regime';
  public acceptButtonText: string = 'Save';
  public cancelButtonText: string = 'Cancel';
  public clientPortfolioAccounts: any[] = [];
  public form: any;
  public authorisedSignatoryPanel: any;
  public beneficiaryPanel: any;
  public openAuthorisedSignatoriesTab: boolean = false;
  
  // Separate arrays for managing authorized signatories and beneficiaries
  public authorisedSignatories: AuthorisedSignatory[] = [];
  public beneficiaries: Beneficiary[] = [];
  public isLoadingAuthorisedSignatories: boolean = false;
  public isLoadingBeneficiaries: boolean = false;

  // Table columns for authorised signatories
  public authorisedSignatoryColumns: TableColumn[] = [
    { key: 'nameValue', label: 'Authorised Signatory name', type: 'text' }
  ];

  // Table columns for beneficiaries
  public beneficiaryColumns: TableColumn[] = [
    { key: 'currencyName', label: 'Currency', type: 'text' },
    { key: 'amount', label: 'Amount', type: 'text' },
    { key: 'beneficiaryName', label: 'Beneficiary Name', type: 'text' },
    { key: 'addressOfBeneficiary', label: 'Address...', type: 'text' },
    { key: 'accountNumber', label: 'Account Number', type: 'text' },
    { key: 'codeIban', label: 'Code IBAN', type: 'text' },
    { key: 'bankName', label: 'Bank', type: 'text' },
    { key: 'bankAddress', label: 'Banking Entity Address', type: 'text' },
    { key: 'swiftCode', label: 'Swift code', type: 'text' },
    { key: 'attention', label: 'Attention', type: 'text' },
    { key: 'reference', label: 'Reference', type: 'text' }
  ];

  constructor(
    private $mdDialog: angular.material.IDialogService,
    private $scope: angular.IScope,
    private clientDetailsService: ClientDetailsService,
    private $element: angular.IAugmentedJQuery,
    private clientCRMRepo: any,
    private $rootScope: angular.IRootScopeService
  ) {}

  public $onInit(): void {
    // Check if we're in edit mode
    this.isEditMode = this.signatoryRegime && this.signatoryRegime.id ? true : false;
    
    if (this.isEditMode) {
      this.title = 'Edit Signatory Regime';
      // Load authorized signatories and beneficiaries separately
      this.loadAuthorisedSignatories();
      this.loadBeneficiaries();
    } else {
      // Initialize with default values for new signatory regime
      this.signatoryRegime = {
        portfolioAccount: '',
        signatoryRegimeType: undefined,
        bank: undefined,
        currency: undefined,
        custodyAccountNumberDebit: '',
        notesSection: '',
        linkToDocument: ''
      } as Partial<SignatoryRegime>;
      
      // Initialize empty arrays for new signatory regime
      this.authorisedSignatories = [];
      this.beneficiaries = [];
    }
  }

  public $postLink(): void {
    // Use a timeout to ensure child components are fully initialized
    this.$scope.$evalAsync(() => {
      this.initializePanelReferences();
      
      // If we need to open the Authorised Signatories tab, do it after a short delay
      if (this.openAuthorisedSignatoriesTab) {
        setTimeout(() => {
          this.openAuthorisedSignatoriesTabProgrammatically();
        }, 200); // Give time for the modal to fully render
      }
    });
  }

  private loadAuthorisedSignatories(): void {
    if (this.signatoryRegime && this.signatoryRegime.id) {
      this.isLoadingAuthorisedSignatories = true;
      this.clientCRMRepo.getAuthorisedSignatories(this.signatoryRegime.id)
        .then((response: any) => {
          this.authorisedSignatories = response.data || [];
          this.isLoadingAuthorisedSignatories = false;
        })
        .catch((error) => {
          console.error('Error loading authorised signatories:', error);
          this.isLoadingAuthorisedSignatories = false;
        });
    }
  }

  private loadBeneficiaries(): void {
    if (this.signatoryRegime && this.signatoryRegime.id) {
      this.isLoadingBeneficiaries = true;
      this.clientCRMRepo.getBeneficiaries(this.signatoryRegime.id)
        .then((response: any) => {
          this.beneficiaries = response.data || [];
          this.isLoadingBeneficiaries = false;
        })
        .catch((error) => {
          console.error('Error loading beneficiaries:', error);
          this.isLoadingBeneficiaries = false;
        });
    }
  }

  private initializePanelReferences(): void {
    // Get reference to the authorised signatory panel component
    const panelElement = this.$element.find('authorised-signatory-panel');
    if (panelElement.length > 0) {
      const scope = panelElement.isolateScope();
      if (scope && (scope as any).$ctrl) {
        this.authorisedSignatoryPanel = (scope as any).$ctrl;
        console.log('Authorised signatory panel found and assigned');
      } else {
        console.log('Authorised signatory panel element found but no controller');
      }
    } else {
      console.log('Authorised signatory panel element not found');
    }

    // Get reference to the beneficiary panel component
    const beneficiaryPanelElement = this.$element.find('beneficiary-panel');
    if (beneficiaryPanelElement.length > 0) {
      const scope = beneficiaryPanelElement.isolateScope();
      if (scope && (scope as any).$ctrl) {
        this.beneficiaryPanel = (scope as any).$ctrl;
        console.log('Beneficiary panel found and assigned');
      } else {
        console.log('Beneficiary panel element found but no controller');
      }
    } else {
      console.log('Beneficiary panel element not found');
    }
  }

  public save(): void {
    // Create a clean copy of the signatory regime data (no validation required)
    const cleanSignatoryRegime = {
      id: this.signatoryRegime.id,
      portfolioAccount: this.signatoryRegime.portfolioAccount,
      signatoryRegimeType: this.signatoryRegime.signatoryRegimeType,
      notesSection: this.signatoryRegime.notesSection,
      linkToDocument: this.signatoryRegime.linkToDocument,
      bank: this.signatoryRegime.bank,
      currency: this.signatoryRegime.currency,
      custodyAccountNumberDebit: this.signatoryRegime.custodyAccountNumberDebit
    };

    // Debug: Log the clean signatory regime data before sending
    console.log('Clean SignatoryRegime data being sent:', JSON.stringify(cleanSignatoryRegime, null, 2));

    // Close dialog and return the clean signatory regime data
    this.$mdDialog.hide(cleanSignatoryRegime);
  }

  public cancel(): void {
    this.$mdDialog.cancel();
  }

  // Authorised Signatory methods
  public addAuthorisedSignatory(): void {
    if (!this.signatoryRegime.id) {
      // Auto-save signatory regime first
      this.saveSignatoryRegimeFirst()
        .then(() => this.openAuthorisedSignatoryPanel())
        .catch(error => this.handleSaveError(error));
    } else {
      this.openAuthorisedSignatoryPanel();
    }
  }

  private openAuthorisedSignatoryPanel(): void {
    if (this.authorisedSignatoryPanel) {
      this.authorisedSignatoryPanel.open();
    } else {
      console.log("No panel to open, attempting to find panel again...");
      // Try to find the panel again in case it wasn't initialized properly
      this.initializePanelReferences();
      if (this.authorisedSignatoryPanel) {
        this.authorisedSignatoryPanel.open();
        console.log("Panel found and opened successfully");
      } else {
        console.log("Still no panel found - check if authorised-signatory-panel component is properly defined");
      }
    }
  }

  public editAuthorisedSignatory(authorisedSignatory: AuthorisedSignatory): void {
    if (this.authorisedSignatoryPanel) {
      this.authorisedSignatoryPanel.open(authorisedSignatory);
    }
  }

  public onAuthorisedSignatorySave(authorisedSignatory: AuthorisedSignatory): void {
    if (!authorisedSignatory) {
      console.error('AuthorisedSignatory parameter is undefined or null');
      return;
    }
    
    // Set loading state
    this.isLoadingAuthorisedSignatories = true;
    
    // Save to backend
    this.saveAuthorisedSignatoryToBackend(authorisedSignatory)
      .then(savedSignatory => {
        // Reload the list after successful save
        this.loadAuthorisedSignatories();
      })
      .catch(error => {
        console.error('Error saving authorised signatory:', error);
        this.isLoadingAuthorisedSignatories = false;
        this.handleSaveError(error);
      });
  }

  public onAuthorisedSignatoryClose(): void {
    // Panel closed, no action needed
  }

  public reopenSignatoryRegime(): void {
    // Close the current modal and reopen it with the same signatory regime
    const currentSignatoryRegime = { ...this.signatoryRegime };
    const currentClient = this.client;
    const currentDropdownOptions = this.dropdownOptions;
    const currentClientPortfolioAccounts = this.clientPortfolioAccounts;
    
    // Close current modal
    this.$mdDialog.hide();
    
    // Reopen the modal with the same data
    setTimeout(() => {
      const tempScope = this.$rootScope.$new(true) as any;
      tempScope.client = currentClient;
      tempScope.signatoryRegime = currentSignatoryRegime;
      tempScope.dropdownOptions = currentDropdownOptions;
      tempScope.clientPortfolioAccounts = currentClientPortfolioAccounts;
      // Add flag to indicate we should open the Authorised Signatories tab
      tempScope.openAuthorisedSignatoriesTab = true;

      this.$mdDialog.show({
        template: '<md-dialog flex="70"><add-signatory-regime-modal client="client" signatory-regime="signatoryRegime" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts" open-authorised-signatories-tab="openAuthorisedSignatoriesTab"></add-signatory-regime-modal></md-dialog>',
        scope: tempScope,
        clickOutsideToClose: true,
        escapeToClose: true
      }).then((result) => {
        console.log('Signatory regime modal reopened with result:', result);
        if (result) {
          // Handle the result if needed
        }
      }).catch((error) => {
        console.error('Error reopening signatory regime modal:', error);
      });
    }, 10); // Small delay to ensure the current modal is fully closed
  }

  public onAuthorisedSignatoryDelete(authorisedSignatory: AuthorisedSignatory): void {
    if (authorisedSignatory && authorisedSignatory.id) {
      // Set loading state
      this.isLoadingAuthorisedSignatories = true;
      
      // Call backend to delete the item
      this.clientCRMRepo.deleteAuthorisedSignatories([authorisedSignatory.id])
        .then(() => {
          // Reload the list after successful deletion
          this.loadAuthorisedSignatories();
        })
        .catch((error) => {
          console.error('Error deleting authorised signatory:', error);
          this.isLoadingAuthorisedSignatories = false;
        });
    }
  }

  // Beneficiary methods
  public addBeneficiary(): void {
    if (!this.signatoryRegime.id) {
      // Auto-save signatory regime first
      this.saveSignatoryRegimeFirst()
        .then(() => this.openBeneficiaryPanel())
        .catch(error => this.handleSaveError(error));
    } else {
      this.openBeneficiaryPanel();
    }
  }

  private openBeneficiaryPanel(): void {
    if (this.beneficiaryPanel) {
      this.beneficiaryPanel.open();
    } else {
      console.log("No beneficiary panel to open, attempting to find panel again...");
      // Try to find the panel again in case it wasn't initialized properly
      this.initializePanelReferences();
      if (this.beneficiaryPanel) {
        this.beneficiaryPanel.open();
        console.log("Beneficiary panel found and opened successfully");
      } else {
        console.log("Still no beneficiary panel found - check if beneficiary-panel component is properly defined");
      }
    }
  }

  public editBeneficiary(beneficiary: Beneficiary): void {
    if (this.beneficiaryPanel) {
      this.beneficiaryPanel.open(beneficiary);
    }
  }

  public onBeneficiarySave(beneficiary: Beneficiary): void {
    if (!beneficiary) {
      console.error('Beneficiary parameter is undefined or null');
      return;
    }
    
    // Set loading state
    this.isLoadingBeneficiaries = true;
    
    // Save to backend
    this.saveBeneficiaryToBackend(beneficiary)
      .then(savedBeneficiary => {
        // Reload the list after successful save
        this.loadBeneficiaries();
      })
      .catch(error => {
        console.error('Error saving beneficiary:', error);
        this.isLoadingBeneficiaries = false;
        this.handleSaveError(error);
      });
  }

  public onBeneficiaryClose(): void {
    // Panel closed, no action needed
  }

  public onBeneficiaryDelete(beneficiary: Beneficiary): void {
    if (beneficiary && beneficiary.id) {
      // Set loading state
      this.isLoadingBeneficiaries = true;
      
      // Call backend to delete the item
      this.clientCRMRepo.deleteBeneficiaries([beneficiary.id])
        .then(() => {
          // Reload the list after successful deletion
          this.loadBeneficiaries();
        })
        .catch((error) => {
          console.error('Error deleting beneficiary:', error);
          this.isLoadingBeneficiaries = false;
        });
    }
  }

  // Delete methods for bulk operations
  public deleteSelectedAuthorisedSignatories(itemIds: number[]): angular.IPromise<void> | void {
    if (itemIds.length === 0) {
      return;
    }

    this.isLoadingAuthorisedSignatories = true;
    return this.clientCRMRepo.deleteAuthorisedSignatories(itemIds)
      .then(() => {
        this.loadAuthorisedSignatories();
      })
      .catch((error) => {
        console.error('Error deleting authorised signatories:', error);
        this.isLoadingAuthorisedSignatories = false;
        throw error;
      });
  }

  public deleteSelectedBeneficiaries(itemIds: number[]): angular.IPromise<void> | void {
    if (itemIds.length === 0) {
      return;
    }

    const confirm = this.$mdDialog.confirm()
      .title('Delete Beneficiaries')
      .textContent(`Are you sure you want to delete ${itemIds.length} beneficiary(ies)?`)
      .ok('Delete')
      .cancel('Cancel');

    return this.$mdDialog.show(confirm).then(() => {
      this.isLoadingBeneficiaries = true;
      return this.clientCRMRepo.deleteBeneficiaries(itemIds)
        .then(() => {
          this.loadBeneficiaries();
        })
        .catch((error) => {
          console.error('Error deleting beneficiaries:', error);
          this.isLoadingBeneficiaries = false;
          throw error;
        });
    });
  }

  // Helper methods for auto-save pattern
  private async saveSignatoryRegimeFirst(): Promise<void> {
    this.isLoading = true;
    
    try {
      // Create a clean copy of the signatory regime data
      const cleanSignatoryRegime = {
        id: this.signatoryRegime.id,
        portfolioAccount: this.signatoryRegime.portfolioAccount,
        signatoryRegimeType: this.signatoryRegime.signatoryRegimeType,
        notesSection: this.signatoryRegime.notesSection,
        linkToDocument: this.signatoryRegime.linkToDocument,
        bank: this.signatoryRegime.bank,
        currency: this.signatoryRegime.currency,
        custodyAccountNumberDebit: this.signatoryRegime.custodyAccountNumberDebit
      };

      // Save signatory regime
      const response = await this.clientCRMRepo.saveSignatoryRegime(this.client.id, cleanSignatoryRegime);
      
      // Update local reference with saved ID
      this.signatoryRegime.id = response.data.id;
      
    } finally {
      this.isLoading = false;
    }
  }

  private async saveAuthorisedSignatoryToBackend(authorisedSignatory: AuthorisedSignatory): Promise<AuthorisedSignatory> {
    const response = await this.clientCRMRepo.saveAuthorisedSignatory(this.signatoryRegime.id, authorisedSignatory);
    return response.data;
  }

  private async saveBeneficiaryToBackend(beneficiary: Beneficiary): Promise<Beneficiary> {
    const response = await this.clientCRMRepo.saveBeneficiary(this.signatoryRegime.id, beneficiary);
    return response.data;
  }

  private openAuthorisedSignatoriesTabProgrammatically(): void {
    // Find the md-tabs element and set the selected index to 1 (Authorised Signatories tab)
    const tabsElement = this.$element.find('md-tabs');
    if (tabsElement.length > 0) {
      // Get the md-tabs controller and set the selected index
      const tabsScope = tabsElement.isolateScope();
      if (tabsScope && (tabsScope as any).$ctrl) {
        (tabsScope as any).$ctrl.selectedIndex = 1;
        console.log('Authorised Signatories tab opened programmatically');
      } else {
        // Fallback: try to find the tabs using Angular Material's API
        const mdTabsCtrl = tabsElement.controller('mdTabs');
        if (mdTabsCtrl) {
          mdTabsCtrl.selectedIndex = 1;
          console.log('Authorised Signatories tab opened programmatically (fallback)');
        }
      }
    }
  }

  private handleSaveError(error: any): void {
    // Show user-friendly error message
    this.$mdDialog.show(
      this.$mdDialog.alert()
        .title('Cannot Add Items')
        .textContent(error.message || 'Please complete required fields first')
        .ok('OK')
    );
  }
}

export const AddSignatoryRegimeModalComponent: angular.IComponentOptions = {
  template: require('./add-signatory-regime.modal.html'),
  controller: AddSignatoryRegimeModalController,
  controllerAs: '$ctrl',
  bindings: {
    client: '<',
    signatoryRegime: '=',
    dropdownOptions: '<',
    clientPortfolioAccounts: '<',
    openAuthorisedSignatoriesTab: '<'
  }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/recipients/modals/add-signatory-regime.modal.ts